import React, { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { AlertTriangle, Edit, Save, X } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import api from '../utils/api';

const severityColors = {
  low: 'bg-green-100 text-green-800 border-green-200',
  medium: 'bg-yellow-100 text-yellow-800 border-yellow-200',
  critical: 'bg-red-100 text-red-800 border-red-200'
};

const severityIcons = {
  low: <AlertTriangle className="h-4 w-4 text-green-600" />,
  medium: <AlertTriangle className="h-4 w-4 text-yellow-600" />,
  critical: <AlertTriangle className="h-4 w-4 text-red-600" />
};

const EditIssueForm = ({ 
  issue, 
  onUpdate, 
  onClose,
  loading = false 
}) => {
  const [formData, setFormData] = useState({
    title: issue.title,
    description: issue.description,
    severity: issue.severity
  });
  
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.title.trim()) {
      newErrors.title = 'Title is required';
    }
    
    if (!formData.description.trim()) {
      newErrors.description = 'Description is required';
    }
    
    if (!formData.severity) {
      newErrors.severity = 'Severity is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    const updateData = {};
    
    // Only include fields that have changed
    if (formData.title.trim() !== issue.title) {
      updateData.title = formData.title.trim();
    }
    
    if (formData.description.trim() !== issue.description) {
      updateData.description = formData.description.trim();
    }
    
    if (formData.severity !== issue.severity) {
      updateData.severity = formData.severity;
    }

    // Don't submit if no changes
    if (Object.keys(updateData).length === 0) {
      toast({
        title: "Info",
        description: "No changes to save",
      });
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await api.put(`/issues/${issue.id}`, updateData);

      const result = response.data;

      if (result.success) {
        toast({
          title: "Success",
          description: "Issue updated successfully",
        });
        
        // Call parent callback
        if (onUpdate) {
          onUpdate(result.data);
        }
        
        onClose();
      } else {
        throw new Error(result.message || 'Failed to update issue');
      }
    } catch (error) {
      console.error('Error updating issue:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to update issue",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const getSeverityColor = () => {
    return severityColors[formData.severity] || severityColors.medium;
  };

  const hasChanges = () => {
    return formData.title.trim() !== issue.title ||
           formData.description.trim() !== issue.description ||
           formData.severity !== issue.severity;
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Edit className="h-5 w-5" />
            Edit Known Issue
          </DialogTitle>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Current Status */}
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  {severityIcons[issue.severity]}
                  <div>
                    <p className="font-semibold">Current Severity</p>
                    <Badge className={severityColors[issue.severity]}>
                      {issue.severity.toUpperCase()}
                    </Badge>
                  </div>
                </div>
                <Badge variant={issue.status === 'open' ? 'default' : 'secondary'}>
                  {issue.status.toUpperCase()}
                </Badge>
              </div>
            </CardContent>
          </Card>

          {/* Title */}
          <div className="space-y-2">
            <label htmlFor="title" className="block text-sm font-medium text-gray-700">
              Issue Title *
            </label>
            <Input
              id="title"
              type="text"
              placeholder="Brief description of the issue..."
              value={formData.title}
              onChange={(e) => handleInputChange('title', e.target.value)}
              className={errors.title ? 'border-red-500' : ''}
              disabled={loading}
            />
            {errors.title && (
              <p className="text-sm text-red-600">{errors.title}</p>
            )}
          </div>

          {/* Severity */}
          <div className="space-y-2">
            <label htmlFor="severity" className="block text-sm font-medium text-gray-700">
              Severity Level *
            </label>
            <Select 
              value={formData.severity} 
              onValueChange={(value) => handleInputChange('severity', value)}
              disabled={loading}
            >
              <SelectTrigger className={errors.severity ? 'border-red-500' : ''}>
                <SelectValue placeholder="Select severity level" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="low">
                  <div className="flex items-center gap-2">
                    {severityIcons.low}
                    <span>Low - Minor issue, limited impact</span>
                  </div>
                </SelectItem>
                <SelectItem value="medium">
                  <div className="flex items-center gap-2">
                    {severityIcons.medium}
                    <span>Medium - Moderate impact</span>
                  </div>
                </SelectItem>
                <SelectItem value="critical">
                  <div className="flex items-center gap-2">
                    {severityIcons.critical}
                    <span>Critical - Major impact</span>
                  </div>
                </SelectItem>
              </SelectContent>
            </Select>
            {errors.severity && (
              <p className="text-sm text-red-600">{errors.severity}</p>
            )}
          </div>

          {/* Description */}
          <div className="space-y-2">
            <label htmlFor="description" className="block text-sm font-medium text-gray-700">
              Description *
            </label>
            <Textarea
              id="description"
              placeholder="Detailed description of the issue..."
              value={formData.description}
              onChange={(e) => handleInputChange('description', e.target.value)}
              rows={6}
              className={errors.description ? 'border-red-500' : ''}
              disabled={loading}
            />
            {errors.description && (
              <p className="text-sm text-red-600">{errors.description}</p>
            )}
          </div>

          {/* Preview */}
          {formData.title && formData.description && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  {severityIcons[formData.severity]}
                  Preview Changes
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div>
                    <h4 className="font-semibold text-sm text-gray-600 mb-1">Title</h4>
                    <p className="text-gray-900">{formData.title}</p>
                    {formData.title !== issue.title && (
                      <p className="text-xs text-gray-500 mt-1">
                        Original: {issue.title}
                      </p>
                    )}
                  </div>
                  <div>
                    <h4 className="font-semibold text-sm text-gray-600 mb-1">Description</h4>
                    <p className="text-gray-700 whitespace-pre-wrap text-sm">
                      {formData.description}
                    </p>
                    {formData.description !== issue.description && (
                      <p className="text-xs text-gray-500 mt-1">
                        Description has been modified
                      </p>
                    )}
                  </div>
                  {formData.severity !== issue.severity && (
                    <div>
                      <h4 className="font-semibold text-sm text-gray-600 mb-1">Severity</h4>
                      <div className="flex items-center gap-2">
                        <Badge className={getSeverityColor()}>
                          {formData.severity.toUpperCase()}
                        </Badge>
                        <span className="text-xs text-gray-500">→</span>
                        <Badge className={severityColors[issue.severity]}>
                          {issue.severity.toUpperCase()}
                        </Badge>
                      </div>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Change Summary */}
          {hasChanges() && (
            <Card className="bg-blue-50 border-blue-200">
              <CardContent className="pt-4">
                <div className="flex items-center gap-2 mb-2">
                  <Edit className="h-4 w-4 text-blue-600" />
                  <p className="font-semibold text-sm text-blue-800">Changes Summary</p>
                </div>
                <ul className="text-sm text-blue-700 space-y-1">
                  {formData.title !== issue.title && (
                    <li>• Title will be updated</li>
                  )}
                  {formData.description !== issue.description && (
                    <li>• Description will be updated</li>
                  )}
                  {formData.severity !== issue.severity && (
                    <li>• Severity will be changed from {issue.severity} to {formData.severity}</li>
                  )}
                </ul>
              </CardContent>
            </Card>
          )}

          {/* Actions */}
          <div className="flex gap-3 pt-4 border-t">
            <Button 
              type="submit" 
              disabled={isSubmitting || !hasChanges()}
              className="flex items-center gap-2"
            >
              {isSubmitting ? (
                <>
                  <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
                  Updating...
                </>
              ) : (
                <>
                  <Save className="h-4 w-4" />
                  Update Issue
                </>
              )}
            </Button>
            
            <Button 
              type="button" 
              variant="outline" 
              onClick={onClose}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default EditIssueForm;